import { ProductType } from '../app.enums';
import { MilkBottleModel } from '../models/milk.model';
import { IOrderFulfillmentContent } from '../models/patient.model';
import { SelectedProductIngredient } from '../models/prep-feed.model';
import { StorageService } from '../services/storage.service';

const BASE_TYPE_PRIORITY: ProductType[] = [
	ProductType.Water,
	ProductType.EBM,
	ProductType.Donor,
	ProductType.RTF,
];

/**
 * Resolves the base ingredient exclusively from the two lists the prep-feed
 * scanning page supplies to the calculator — scanned milk bottles and
 * manually/scan-selected product ingredients. No other source of base
 * information is accepted, ensuring the calculator never inherits base
 * selection from EHR order fulfillments or external heuristics.
 *
 * Type priority (Water > EBM > Donor > RTF) determines which base wins.
 * Within the same type, the base with the lowest caloriesPerUnit is chosen.
 * Returns null when neither list contains a resolvable base product.
 */
export function resolveBase(
	milkBottles: MilkBottleModel[],
	ingredients: SelectedProductIngredient[],
): IOrderFulfillmentContent | null {
	const bases = getUniqueBases(milkBottles, ingredients);
	for (const type of BASE_TYPE_PRIORITY) {
		const match = bases.find((c) => c.type === type);
		if (match) return match;
	}
	return bases[0] ?? null;
}

/**
 * Returns all unique base products found in the scanned milk bottles and
 * selected ingredients, sorted by caloriesPerUnit ascending (lowest first).
 */
export function getUniqueBases(
	milkBottles: MilkBottleModel[],
	ingredients: SelectedProductIngredient[],
): IOrderFulfillmentContent[] {
	const productIdSet = new Set<string>();

	for (const ingredient of ingredients) {
		const id = ingredient?.content?.productId;
		if (id) productIdSet.add(String(id));
	}

	for (const mb of milkBottles) {
		for (const c of mb.contents ?? []) {
			if (c.contentProductId) productIdSet.add(String(c.contentProductId));
		}
	}

	return [...productIdSet]
		.map((id) => StorageService.configs?.getProduct(id))
		.filter((product) => product?.isBase)
		.sort((a, b) => (a.caloriesPerUnit ?? 0) - (b.caloriesPerUnit ?? 0))
		.map((product) => ({
			productId: product.id,
			productName: product.name,
			type: product.type,
			isBase: product.isBase,
		}));
}
